import { css } from '@emotion/css';
import creased from '@volumental/assets/icons/paper/creased.png';
import moreThanOne from '@volumental/assets/icons/paper/papers.png';
import notebook from '@volumental/assets/icons/paper/notebook.png';
import { ImageBulletPoints, TextButton } from '@volumental/common';
import { useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import { PaperFormat } from '../../../api/gql/graphql';
import { Modal } from '../../../components/modal/modal';
import { useScannerStore, useSessionStore } from '../../../store';
import { PaperButton } from './paper-button';

const descriptionStyle = css`
  padding-bottom: 1rem;
`;

const instructionStyle = css`
  font-size: 21px;
  padding-bottom: 2rem;
`;

interface PaperSizeCardProps {
  show: boolean;
  onReady: () => void;
  onClose: () => void;
  preferredReferenceObject: PaperFormat | undefined;
}

export function PaperSizeCard({
  show,
  onReady,
  onClose,
  preferredReferenceObject,
}: PaperSizeCardProps): JSX.Element {
  const { t } = useTranslation();
  const { flowType, setPaperFormat } = useScannerStore((store) => store);
  const { mode } = useSessionStore((store) => store);

  const [paperType, setPaperType] = useState(preferredReferenceObject);
  const bullets = [
    {
      img: creased,
      text: t('onboarding.paperSizeBullet1'),
    },
    {
      img: moreThanOne,
      text: t('onboarding.paperSizeBullet2'),
    },
    {
      img: notebook,
      text: t('onboarding.paperSizeBullet3'),
    },
  ];

  const changePaperType = (from: PaperFormat): void => {
    const to = from === PaperFormat.A4 ? PaperFormat.Letter : PaperFormat.A4;
    setPaperType(to);
    usageTracker.track(UsageTrackerEvent.PaperTypeChange, {
      sizing_flow_type: flowType,
      experience: mode,
      format_changed_to: to,
    });
  };

  const selectPaperFormat = (format: PaperFormat): void => {
    setPaperFormat(format);
    usageTracker.track(UsageTrackerEvent.PaperSelected, {
      sizing_flow_type: flowType,
      experience: mode,
      paper: format.toLocaleLowerCase(),
    });
    onReady();
  };

  return (
    <Modal
      show={show}
      close={onClose}
      modalTitle={t('modalTitles.paperSize')}
      fitContent
    >
      <div className={instructionStyle}>
        <Trans>{t('onboarding.paperSizeDescription')}</Trans>
        <ImageBulletPoints bulletPoints={bullets} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        {paperType ? (
          <>
            <div className={descriptionStyle}>
              {t('paperSize.paperFormatCheck')}
            </div>
            <PaperButton
              selectPaperFormat={selectPaperFormat}
              paperType={paperType}
            />
            <TextButton
              style={{ marginTop: '0px' }}
              onClick={() => {
                changePaperType(paperType);
              }}
            >
              {t('button.useDifferentPaperType', {
                paper:
                  paperType === PaperFormat.A4
                    ? t('paperSize.sizeUsLetter')
                    : t('paperSize.sizeA4'),
              })}
            </TextButton>
          </>
        ) : (
          <>
            <div className={descriptionStyle}>
              {t('paperSize.paperFormatYouGot')}
            </div>
            <PaperButton
              selectPaperFormat={selectPaperFormat}
              paperType={PaperFormat.Letter}
            />
            <PaperButton
              selectPaperFormat={selectPaperFormat}
              paperType={PaperFormat.A4}
            />
          </>
        )}
      </div>
    </Modal>
  );
}
